import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Container,
    Grid,
    Card,
    CardContent,
    Link,
    Avatar,
    Paper,
    ClickAwayListener,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Drawer,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Global, css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InquiryImage from '@/assets/images/inquiry.webp';
import LoginImage from '@/assets/images/login.webp';
import ViewDetailImage from '@/assets/images/viewDetail.webp';
import CenturyOldStyleFont from '@/assets/fonts/century-old-style-std-regular.otf';
import { logOut, vendorLogOut } from '@/services/auth.service';
import { isLogined } from '@/services/mallUser.service';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { useVendorLoginDialogStore } from '@/store/vendorLoginDialogStore';
import { useUserStore } from '@/store/userStore';
import { useVendorStore } from '@/store/vendorStore';
import config from '@/config/config';
import Shop3LoginDialog from '@/components/Dialog/Shop3LoginDialog';
import VendorLoginDialog from '@/components/Dialog/VendorLoginDialog';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// 네비게이션 메뉴 항목
const NAV_MENU_ITEMS = ['SHOP3', 'Market', 'Business', 'Problem', 'Solution', 'Customer', 'Product', 'Team'];

// TopNavigation Props 타입
interface TopNavigationProps {
    activeMenu?: string;
    onMenuChange?: (menu: string) => void;
}

// 탑 네비게이션 컴포넌트
export const TopNavigation = ({ activeMenu = '', onMenuChange = () => {} }: TopNavigationProps) => {
    const navigate = useNavigate();
    const { vendorPath } = useParams();
    const { setIsOpen } = useLoginFormDialogStore((state) => state.action);
    const { setIsOpen: setVendorLoginOpen } = useVendorLoginDialogStore();
    const vendorAccessToken = useVendorStore((state) => state.accessToken);
    const vendorInfo = useVendorStore((state) => state.vendorInfo);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isVendorLoggedIn, setIsVendorLoggedIn] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [userEmail, setUserEmail] = useState<string>('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // 반응형 브레이크포인트
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px ~ 900px

    useEffect(() => {
        // 로그인 상태 확인
        setIsLoggedIn(isLogined());
        setIsVendorLoggedIn(!!vendorAccessToken);

        // 벤더 이메일 정보 가져오기
        if (vendorInfo?.email && vendorAccessToken) {
            setUserEmail(vendorInfo.email);
        }
    }, [vendorAccessToken, vendorInfo]);

    // 로그인 상태 변경 감지를 위한 추가 useEffect
    useEffect(() => {
        const checkLoginStatus = () => {
            setIsLoggedIn(isLogined());
            setIsVendorLoggedIn(!!vendorAccessToken);
        };

        // 주기적으로 로그인 상태 확인 (1초마다)
        const interval = setInterval(checkLoginStatus, 1000);

        return () => clearInterval(interval);
    }, [vendorAccessToken]);

    const handleUserIconClick = () => {
        // 메인 페이지에서는 seller 로그인 비활성화
        if (vendorPath) {
            if (isLoggedIn) {
                setIsDropdownOpen(!isDropdownOpen);
            } else {
                setIsOpen(true);
            }
        } else {
            // 메인 페이지에서는 vendor 로그인만 사용
            if (isVendorLoggedIn) {
                setIsDropdownOpen(!isDropdownOpen);
            } else {
                setVendorLoginOpen(true);
            }
        }
    };

    const handleProfileClose = () => {
        setIsDropdownOpen(false);
    };

    const handleLogout = async () => {
        try {
            if (isVendorLoggedIn) {
                await vendorLogOut();
            } else {
                await logOut();
            }
            setIsLoggedIn(false);
            setIsVendorLoggedIn(false);
            setUserEmail('');
            handleProfileClose();
            // 로그아웃 후 현재 경로 유지하면서 페이지 새로고침
            window.location.reload();
        } catch (error) {
            console.error('로그아웃 오류:', error);
        }
    };

    const handleAccountSettings = () => {
        navigate('/account/settings');
        handleProfileClose();
    };

    const handleAdminRedirect = () => {
        // 운영환경에 따라 관리자 페이지 URL 결정
        const adminUrl = config.url.matching_url;

        window.open(adminUrl, '_blank');
        handleProfileClose();
    };

    const getInitials = (email: string) => {
        return email.charAt(0).toUpperCase();
    };

    return (
        <AppBar
            position="sticky"
            sx={{
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                color: '#333',
            }}
        >
            {/* 상단: 로고 + 버튼들 */}
            <Toolbar sx={{ justifyContent: 'space-between', px: 5 }}>
                <Box
                    component="img"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202601/2d6577e55d24c2f340edff8bf098757f.png"
                    alt="Brand Logo"
                    onClick={() => {
                        onMenuChange('SHOP3');
                        navigate('/');
                    }}
                    sx={{
                        height: '40px',
                        cursor: 'pointer',
                        objectFit: 'contain',
                    }}
                />

                <Box flex={1} />

                {/* 메뉴 영역 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/subscription')}
                        startIcon={<CreditCardIcon />}
                        sx={{
                            backgroundColor: '#FB6D48',
                            borderRadius: '8px',
                        }}
                    >
                        서비스플랜
                    </Button>
                    {/* 로그인/프로필 버튼 */}
                    <Box sx={{ position: 'relative' }}>
                        {(vendorPath ? isLoggedIn : isVendorLoggedIn) ? (
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    data-profile-button
                                    onClick={handleUserIconClick}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        p: 0,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: '#FFD1D9',
                                            color: 'rgb(151, 53, 98)',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            border: '2px solid #374151',
                                        }}
                                    >
                                        {userEmail ? getInitials(userEmail) : 'U'}
                                    </Avatar>
                                </IconButton>
                                {/* 화살표 버튼 - 로그인 상태일 때만 표시 */}
                                <IconButton
                                    data-profile-button
                                    onClick={handleUserIconClick}
                                    sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -2,
                                        width: 18,
                                        height: 18,
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        p: 0,
                                    }}
                                >
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            fontSize: 14,
                                            color: '#6b7280',
                                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease-in-out',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        ) : (
                            <Button
                                onClick={() => {
                                    window.open(config.url.matching_url, '_blank');
                                }}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#FB6D48',
                                    borderRadius: '8px',
                                }}
                            >
                                로그인
                            </Button>
                        )}

                        {/* 프로필 드롭다운 */}
                        {isDropdownOpen && (
                            <ClickAwayListener
                                onClickAway={(event) => {
                                    // 프로필 버튼 클릭은 무시 (토글을 위해)
                                    const target = event.target as Element;
                                    if (target.closest('[data-profile-button]')) {
                                        return;
                                    }
                                    handleProfileClose();
                                }}
                            >
                                <Paper
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        mt: 1,
                                        borderRadius: 1,
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                        border: '1px solid #e0e0e0',
                                        minWidth: 280,
                                        zIndex: 1300,
                                        overflow: 'visible',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -8,
                                            right: 24,
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid white',
                                            zIndex: 1,
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -9,
                                            right: 24,
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid #e0e0e0',
                                            zIndex: 0,
                                        },
                                    }}
                                >
                                    {/* 사용자 정보 섹션 */}
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: '#FFD1D9',
                                                    color: 'rgb(151, 53, 98)',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {userEmail ? getInitials(userEmail) : 'V'}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                                                {userEmail || '벤더'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider />

                                    {/* Account settings 섹션 */}
                                    <List sx={{ p: 0 }}>
                                        <ListItem
                                            component="button"
                                            onClick={handleAccountSettings}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <PersonIcon sx={{ color: '#666', fontSize: 20 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="계정 설정"
                                                primaryTypographyProps={{
                                                    fontSize: '14px',
                                                    color: '#333',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem
                                            component="button"
                                            onClick={handleAdminRedirect}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <AdminPanelSettingsIcon sx={{ color: '#666', fontSize: 20 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="관리자로 이동"
                                                primaryTypographyProps={{
                                                    fontSize: '14px',
                                                    color: '#333',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </ListItem>
                                    </List>

                                    <Divider />

                                    {/* 로그아웃 버튼 */}
                                    <Box sx={{ p: 1.5 }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={handleLogout}
                                            sx={{
                                                borderColor: '#ddd',
                                                color: '#333',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                py: 0.75,
                                                borderRadius: 2,
                                                '&:hover': {
                                                    borderColor: '#999',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            로그아웃
                                        </Button>
                                    </Box>
                                </Paper>
                            </ClickAwayListener>
                        )}
                    </Box>
                    <IconButton
                        sx={{
                            color: '#333',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                        aria-label="상담"
                        href="http://pf.kakao.com/_FxgxkUn/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Box
                            component="img"
                            src={InquiryImage}
                            alt="Inquiry"
                            sx={{
                                height: 24,
                                width: 24,
                                objectFit: 'contain',
                            }}
                        />
                    </IconButton>
                </Box>
            </Toolbar>

            {/* 하단: 네비게이션 메뉴 - 데스크톱/태블릿 */}
            {!isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 1,
                        overflowX: isTablet ? 'auto' : 'visible',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                    }}
                >
                    {NAV_MENU_ITEMS.map((item) => (
                        <Button
                            key={item}
                            onClick={() => onMenuChange(item)}
                            sx={{
                                color: item === activeMenu ? '#FB6D48' : '#333',
                                fontWeight: item === activeMenu ? 600 : 500,
                                fontSize: isTablet ? '14px' : '17.6px',
                                textTransform: 'none',
                                px: isTablet ? 1.5 : 2.5,
                                py: 1,
                                minWidth: 'auto',
                                position: 'relative',
                                borderRadius: 0,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#FB6D48',
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: item === activeMenu ? '60%' : '0%',
                                    height: '2px',
                                    backgroundColor: '#FB6D48',
                                    transition: 'width 0.2s ease-in-out',
                                },
                            }}
                        >
                            {item}
                        </Button>
                    ))}
                </Box>
            )}

            {/* 하단: 햄버거 메뉴 버튼 - 모바일 */}
            {isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 0.5,
                    }}
                >
                    <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ color: '#333' }} aria-label="메뉴 열기">
                        <MenuIcon />
                    </IconButton>
                </Box>
            )}

            {/* 모바일 메뉴 Drawer */}
            <Drawer
                anchor="bottom"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '70vh',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Drawer 헤더 */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            메뉴
                        </Typography>
                        <IconButton onClick={() => setMobileMenuOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {/* 메뉴 항목들 */}
                    <List sx={{ p: 0 }}>
                        {NAV_MENU_ITEMS.map((item) => (
                            <ListItem
                                key={item}
                                component="button"
                                onClick={() => {
                                    onMenuChange(item);
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    border: 'none',
                                    backgroundColor: item === activeMenu ? 'rgba(251, 109, 72, 0.08)' : 'transparent',
                                    cursor: 'pointer',
                                    borderLeft: item === activeMenu ? '3px solid #FB6D48' : '3px solid transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'rgba(251, 109, 72, 0.08)',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={item}
                                    slotProps={{
                                        primary: {
                                            sx: {
                                                fontSize: '16px',
                                                fontWeight: item === activeMenu ? 600 : 500,
                                                color: item === activeMenu ? '#FB6D48' : '#333',
                                            },
                                        },
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </AppBar>
    );
};

// 히어로 섹션 컴포넌트
const HeroSection = () => {
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleVideoLoaded = () => {
        setIsVideoLoaded(true);
    };

    const handleVideoError = () => {
        setHasError(true);
    };

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#000',
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            {/* 폴백 이미지 - 로딩 중이거나 에러 발생 시 표시 */}
            {(!isVideoLoaded || hasError) && (
                <Box
                    component="img"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/8b0731d36b6510936e862f7b93e1e35c.png"
                    alt="Hero Image"
                    sx={{
                        maxWidth: '1920px',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        margin: 'auto',
                        display: !isVideoLoaded || hasError ? 'block' : 'none',
                    }}
                />
            )}

            {/* 비디오 - 로드 완료 후 표시 */}
            {!hasError && (
                <Box
                    component="video"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/93ba3d4828a2fd14acbb1871b571d746.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={handleVideoLoaded}
                    onError={handleVideoError}
                    sx={{
                        maxWidth: '1920px',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        margin: 'auto',
                        display: isVideoLoaded && !hasError ? 'block' : 'none',
                    }}
                />
            )}
        </Box>
    );
};

// 이미지 배너 섹션 컴포넌트
const ImageBannerSection = () => {
    return (
        <Box
            sx={{
                py: 12,
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 4,
                        alignItems: 'stretch',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/PRD/202512/f847cfe5b7dd7921643cafeb5a2d565c.png"
                        alt="Banner Image 1"
                        sx={{
                            height: '314px',
                            objectFit: 'contain',
                        }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/PRD/202512/bc94fa9525654f8a015ac78ae4469253.gif"
                        alt="Banner Image 2"
                        sx={{
                            maxWidth: '50%',
                            height: '314px',
                            objectFit: 'contain',
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

// AI Studio 섹션 컴포넌트
const AIStudioSection = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<number | null>(null);

    const handleOpenDialog = (index: number) => {
        setSelectedFeatureIndex(index);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const aiStudioFeatures = [
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/6935e2bff043c1806523ce2ffb30831b.webp',
            title: 'Face image\nMulti modal',
            description: `텍스트와 이미지를 동시에 두 정보를 결합해 분석하여 모델 이미지 생성 모델 얼굴 영역을 자동으로 인식하고, 해당 부분의 디테일을 향상시키는 모델`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/536465b09b9255f96434eabc5393e3c1.webp',
            title: 'Parameter Tuning\nPrompt Engineering',
            description: `얼굴과 체형은 변하지 않고 모델이 동작과 포즈만 변경 얼굴이나 이미지 손상이 이루어 지지 않으며 옷의 형태도 손상 되지 않음`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/9d8a1c1e8e219e9d7a3fdf8e3cff76e5.webp',
            title: 'Image Segmentation\nAI Background',
            description: `다양한 배경을 생성하여 이미지 세그먼트 기술을 통해 생성된 AI 모델의 동작이 자연스럽게 배경에 입혀질 수 있도록 만들어 줍니다.`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/f3d7eb4bddd10c3d2ff3c724af697529.webp',
            title: 'AI Fashion Rendering',
            description: `패션 상품을 AI가 스타일 있게 연출하고 보여주는 이미지 생성 기술 옷을 옷걸이나 마네킹에 자연스럽게 배치하거나, 배경, 조명, 소품까지 조화롭게 만들어줌`,
        },
    ];

    return (
        <Box sx={{ py: 12, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        sx={{
                            color: '#333',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 AI Studio
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                            mb: 1,
                        }}
                    >
                        샵쓰리는 온에스트레이션과 방식을 적용해 피팅을 진행합니다.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        여러 AI 공정을 정교하게 조율하여 더욱 완성도 높은 결과로 만들어 드립니다.
                    </Typography>
                </Box>

                {/* 카드 그리드 컨테이너 */}
                <Box
                    sx={{
                        backgroundColor: '#2f3438',
                        borderRadius: '30px',
                        p: 4,
                        position: 'relative',
                    }}
                >
                    <Grid container spacing={1}>
                        {aiStudioFeatures.map((feature, index) => (
                            <Grid size={3} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        backgroundColor: '#35393c',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {/* 이미지 섹션 */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            aspectRatio: '297/431',
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.image}
                                            alt={feature.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </Box>

                                    {/* 컨텐츠 섹션 */}
                                    <CardContent
                                        sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '180px',
                                            position: 'relative',
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minHeight: '70px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    fontSize: '1.1rem',
                                                    whiteSpace: 'pre-line',
                                                    textAlign: 'center',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                minHeight: '80px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                mb: 2,
                                                flex: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#ccc',
                                                    lineHeight: 1.5,
                                                    fontSize: '0.8rem',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                        {/* 3점 아이콘 */}
                                        <Box height={'20px'} />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                pb: 1,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                component={'img'}
                                                src={ViewDetailImage}
                                                alt="View Details"
                                                onClick={() => handleOpenDialog(index)}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    '&:hover': {
                                                        cursor: 'pointer',
                                                    },
                                                    mb: 1,
                                                }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* 오버레이 다이얼로그 */}
                    {openDialog && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                            }}
                            onClick={handleCloseDialog}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#2f3438',
                                    borderRadius: '30px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* 상단 헤더 */}
                                <Box
                                    sx={{
                                        backgroundColor: '#2f3438',
                                        color: 'white',
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {/* 닫기 버튼 */}
                                    <IconButton
                                        onClick={handleCloseDialog}
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: 16,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>

                                    {/* 별점 */}
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            mb: 1,
                                            letterSpacing: 2,
                                        }}
                                    >
                                        ★★★★★
                                    </Typography>

                                    {/* 제목 */}
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontWeight: 'normal',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {selectedFeatureIndex !== null
                                            ? aiStudioFeatures[selectedFeatureIndex].title
                                            : ''}
                                    </Typography>
                                </Box>

                                {/* 하단 컨텐츠 영역 */}
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        p: 4,
                                        flex: 1,
                                        overflowY: 'auto',
                                    }}
                                    data-hint="하단 컨텐츠 영역"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 3,
                                        }}
                                    >
                                        {(() => {
                                            switch (selectedFeatureIndex) {
                                                case 0:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                gap: 1,
                                                                alignItems: 'flex-start',
                                                            }}
                                                        >
                                                            {/* 왼쪽 */}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flex: 0.8,
                                                                    flexDirection: 'row',
                                                                    alignItems: 'flex-start',
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/85e28ad265a7c078e1da5cdf7983dddb.png"
                                                                    alt="Face Image 멀티 모달"
                                                                    sx={{
                                                                        width: '40%',
                                                                        height: '250px',
                                                                        objectFit: 'contain',
                                                                        aspectRatio: '1/1',
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'flex-start',
                                                                        alignItems: 'flex-start',
                                                                        ml: 2,
                                                                        mt: 7,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="h1"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                mb: 2,
                                                                                color: '#333',
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            Face Image 멀티 모달
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: '#666',
                                                                                lineHeight: 1.8,
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            모델은 멀티모달 방식을 통해 텍스트와
                                                                            이미지를 동시에 입력받아 두 정보를 결합해
                                                                            분석합니다. 이를 기반으로 한국 고객들이
                                                                            선호하는 모델 이미지를 생성합니다.
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {/* 오른쪽 */}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flex: 1,
                                                                    flexDirection: 'row',
                                                                    alignItems: 'flex-start',
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/12de23c1e0dacbdbde08375db418430a.png"
                                                                    alt="Face Detailer"
                                                                    sx={{
                                                                        width: '42%',
                                                                        height: '550px',
                                                                        objectFit: 'contain',
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'flex-start',
                                                                        ml: 2,
                                                                        mt: 7,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="h1"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                mb: 2,
                                                                                color: '#333',
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            Face Detailer
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: '#666',
                                                                                lineHeight: 1.8,
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            모델은 얼굴 영역을 자동으로 인식하고, 해당
                                                                            부분의 해상도와 선명도, 디테일을 개선하여
                                                                            더욱 선명하고 자연스러운 이미지를 만들어
                                                                            줍니다.
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 1:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/8937233180c06833231761340213ebee.webp"
                                                                alt="Face Swap Model"
                                                                sx={{
                                                                    width: '45%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {/* Face Swap */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Face Swap
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        생성된 모델의 얼굴이 자연스럽게 그대로
                                                                        유지되도록 합니다.
                                                                    </Typography>
                                                                </Box>

                                                                {/* Parameter Tuning Prompt Engineering */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Parameter Tuning Prompt Engineering
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        모델 자체를 변경하지 않고 입력 문장(프롬프트)을
                                                                        설계하여 반응을 조정하는 기법입니다. 이는 모델의
                                                                        학습이나 추론 과정에서 하이퍼파라미터나 내부
                                                                        설정값을 조정하는 방식으로 이루어집니다. 이
                                                                        과정을 통해 모델의 동작은 달라질 수 있지만,
                                                                        얼굴이나 이미지가 손상되지 않고 옷의 형태 또한
                                                                        그대로 유지되도록 합니다.
                                                                    </Typography>
                                                                </Box>

                                                                {/* Identity-Preserving Pose Generation */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Identity-Preserving Pose Generation
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        신원 보존 포즈 생성’은 얼굴이나 체형 등 모델의
                                                                        핵심 특성을 그대로 유지하면서, 동작이나 포즈만
                                                                        자연스럽게 바꿔주는 기술입니다. 이 과정에서
                                                                        모델의 움직임은 달라지지만, 얼굴이나 이미지가
                                                                        손상되지 않고 옷의 형태도 온전히 보존됩니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 2:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/fe258d9b363469967ac72f3679400176.webp"
                                                                alt="Case 2 Image"
                                                                sx={{
                                                                    width: '53%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Image Segmentation AI Background
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        다양한 배경을 생성하고, 이미지 세그먼트 기술을
                                                                        활용하여 생성된 AI 모델의 동작이 더욱 자연스럽게
                                                                        배경과 어우러지도록 구현합니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 3:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/ea280f4e3e10b763c9311738fe9d034f.webp"
                                                                alt="Case 3 Image"
                                                                sx={{
                                                                    width: '63%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {/* 단일 단락 */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        AI Fashion Rendering
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        AI가 패션 상품을 세련되게 연출해 보여주는 이미지
                                                                        생성 기술입니다. 옷을 옷걸이나 마네킹에
                                                                        자연스럽게 배치하고, 배경·조명·소품까지 조화롭게
                                                                        구성하여 더욱 감각적인 이미지를 완성합니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                            }
                                            return <></>;
                                        })()}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

// SHOP3 Cut Type 섹션 컴포넌트
const CutTypeSection = () => {
    const cutTypes = [
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/93f30d6541fb8024053a103d0877fd90.webp',
            title: '모델컷',
            description:
                '상품의 매력을 가장 잘 살려줄 1000% 이상의 다양한 AI 모델과 상품을 매칭해 가장 멋나게 핏합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/94dc7ef211565a71db78e0088485cd8b.webp',
            title: '배경컷',
            description:
                '내 상품은 입소의 시간과 구애를 받지 않고 어디서든 어울려며, 색감도 반한 연출을 생생 가지를 제공합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/86d5389619901fa04da8e892872de460.webp',
            title: '소품컷',
            description:
                '고객은 제품이 실제로 어울리는지 미리 확인하고 싶어합니다. 다양한 소품 매칭은 고객의 이용을 증진시킵니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/98ff98b907dac2186370c147d9abc3ee.webp',
            title: '옷걸이컷',
            description: '조잡한 촬영을 사라지게 합니다. 깔끔한 눈높이 각도로도 소장 욕구를 자극합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/22f6a1a54e5f9fc769656f7f558e2b53.webp',
            title: '세팅컷',
            description: '다양한 방식으로 물건적 상황과 것이 안정감과 구매 가치를 높입니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/79f4fd2a5b638306f27c931a4ae4efd4.webp',
            title: '포커스컷',
            description: '원단의 질감과 디테일한 디자인을 생생하게 전달합니다.',
        },
    ];

    return (
        <Box sx={{ py: 12, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: '#333',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 Cut Type
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        샵쓰리에는 6가지의 컷이 있습니다.
                    </Typography>
                </Box>

                {/* 카드 그리드 컨테이너 */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 10,
                        padding: '0 7%',
                    }}
                >
                    {cutTypes.map((cutType, index) => (
                        <Card
                            key={index}
                            sx={{
                                height: '100%',
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={cutType.image}
                                    alt={cutType.title}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                            <CardContent
                                sx={{
                                    p: { xs: 0, md: 2 },
                                    textAlign: 'center',
                                    border: 'none',
                                    boxShadow: 'none',
                                }}
                            >
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: '#333',
                                        mb: 1.5,
                                    }}
                                >
                                    {cutType.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.6,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {cutType.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

// 섹션 3 (플레이스홀더)
const Section3 = () => {
    return (
        <Box
            sx={{
                py: 10,
                background:
                    'url("https://d1vb419ubgm46b.cloudfront.net/DEV/202509/e3c640b4693b418230fd283441b50f3e.webp") center center / cover no-repeat fixed',
                backgroundSize: 'cover',
                minHeight: '1300px',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 6, mt: 10 }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: 'white',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 Post Hub Mobile
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'white',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        AI로 만들어진 모델 사진을 자동으로 SNS(인스타그램, 틱톡, 카카오스토리)에 공유됩니다
                    </Typography>
                </Box>
                <Box display={'flex'} justifyContent={'center'} gap={3}>
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/340047cf9a30ad2b89ac89f7d872fcbc.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/8b2611f9eadd07441f8fce57972e7291.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/831bd83a996c65f86c355efe0cb6833c.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                </Box>
            </Container>
            <Box
                component="img"
                position={'relative'}
                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/dd006d672c3f3346f0fc8813463cd65b.webp"
                alt="Section 1 Image"
                sx={{ width: '110%', left: '-5%', mt: 5 }}
            />
        </Box>
    );
};
// 섹션 4 (플레이스홀더)
const Section4 = () => {
    return (
        <Box sx={{ py: 10, backgroundColor: '#f0f0f0' }}>
            <Container maxWidth="lg">
                <Typography variant="h4" align="center" color="#666">
                    궁금하신 점은 언제든지 문의해 주세요. 전문 상담원이 친절하게 안내해 드리겠습니다.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 1.5,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #bbb',
                            },
                        }}
                        href="http://pf.kakao.com/_FxgxkUn/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                background: 'transparent',
                            }}
                            component={'img'}
                            src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/83588f4d9c45901c96f30ade88ef5086.webp"
                        />
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#333',
                            }}
                        >
                            상담 요청하기
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '1.2rem' }}>›</Typography>
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

// 푸터 섹션 컴포넌트
const FooterSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: '#2a2a2a',
                color: 'white',
                py: 6,
                marginTop: 'auto',
            }}
        >
            <Container maxWidth="lg">
                {/* 상단 링크 영역 */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        mb: 4,
                        fontSize: '0.95rem',
                    }}
                >
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        운영정책
                    </Link>
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        서비스 약관
                    </Link>
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        개인정보 보호방침
                    </Link>
                </Box>

                {/* 회사 정보 영역 */}
                <Box
                    sx={{
                        fontSize: '0.85rem',
                        lineHeight: 1.8,
                        color: '#aaa',
                        mb: 3,
                    }}
                >
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        상호명: 플로잉스페이스(주)
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>대표이사: 오석환</Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        플로잉 서비스 고객센터 이메일: help@flowing.tel
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        주소: 서울 강동구 길동 천호대로 1121 일성하이츠 205
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        사업자등록번호 650-86-03029 통신판매신고: 제 2024-서울강동-1600 호 개인정보관리 책임자: 손유한
                        전화번호: 070-4504-4055 문의: help@flowing.tel
                    </Typography>
                </Box>

                {/* 저작권 영역 */}
                <Typography
                    sx={{
                        fontSize: '0.85rem',
                        color: '#888',
                    }}
                >
                    2022 Flowing Corp.
                </Typography>
            </Container>
        </Box>
    );
};

// Market 페이지 컴포넌트
const MarketSection = () => {
    return (
        <Box sx={{ flex: 1 }}>
            {/* 히어로 배너 */}
            <Box
                sx={{
                    position: 'relative',
                    height: '300px',
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(https://d1vb419ubgm46b.cloudfront.net/PRD/202601/8b479e69aaeb1f09577fdcbdcc31d60e.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            ></Box>

            {/* 시장 규모 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    시장 규모
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 6 }}>
                    Market Size
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ color: '#333', lineHeight: 2, mb: 4, fontSize: '18px' }}>
                        연간 20조 원 규모의 거래가 이루어지는 동대문 시장에서는 매일 3만 건, 연간 1,000만 건 이상의
                        신제품이 출시됩니다. 하지만 벌당 최대 7만 원에 달하는 막대한 촬영 비용(모델료, 스튜디오 대관
                        등)은 도매상들에게 큰 경제적 부담이 되고 있습니다. 우리는 AI 접목 피팅 기술을 통해 이러한 비용을
                        획기적으로 절감하고 패션 산업의 디지털 전환을 이끌고자 합니다.
                        <br />
                        그리고 우리의 연구와 기술을 구글 솔루션과 인프라는 접목 하여 이 시장의 문제점을 해결 하고자
                        합니다.
                    </Typography>

                    <Typography sx={{ color: '#666', lineHeight: 2, fontStyle: 'italic', fontSize: '16px' }}>
                        In the Dongdaemun market, where annual transactions reach 20 trillion KRW, over 30,000 new
                        products are released daily, amounting to more than 10 million annually. However, the massive
                        photography costs—reaching up to 70,000 KRW per garment (including model fees and studio
                        rentals)—pose a significant economic burden on wholesalers. We aim to drastically reduce these
                        costs and lead the digital transformation of the fashion industry through AI-integrated fitting
                        technology.
                        <br />
                        We intend to solve the market's problems by combining our research and technology with Google's
                        solutions and infrastructure.
                    </Typography>
                </Box>
            </Container>

            {/* 시장 상황 요약 섹션 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                        시장 상황 요약
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#666', mb: 6 }}>
                        Market Situation Summary
                    </Typography>

                    <Grid container spacing={6} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202601/faf613b1f8d531739a4ea5ec8d510c72.webp"
                                alt="Market Situation"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 9 }}>
                            <Box
                                component="ul"
                                sx={{ pl: 3, '& li': { mb: 3, color: '#333', lineHeight: 1.9, fontSize: '18px' } }}
                            >
                                <li>
                                    <strong>거래 규모:</strong> 연간 약 15조~20조 원
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '16px' }}>
                                        (대한민국 패션 유통 시장의 약 40% 점유)
                                    </Typography>
                                </li>
                                <li>
                                    <strong>신상 출시:</strong> 일평균 약 2만~3만건, 연간 최대 1,000만 건의 새로운
                                    디자인 등장
                                </li>
                                <li>
                                    <strong>촬영 비용:</strong> 의류 한 벌당 약 2만~7만 원
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '16px' }}>
                                        (전통적인 방식의 모델 및 스튜디오 촬영 기준)
                                    </Typography>
                                </li>
                            </Box>

                            <Divider sx={{ my: 4 }} />

                            <Box
                                component="ul"
                                sx={{ pl: 3, '& li': { mb: 2, color: '#666', lineHeight: 1.8, fontSize: '16px' } }}
                            >
                                <li>
                                    Transaction Volume: Approximately 15-20 trillion KRW annually
                                    <br />
                                    (Accounts for about 40% of the South Korean fashion retail market).
                                </li>
                                <li>
                                    New Product Launches: Approx. 20,000-30,000 daily; up to 10 million new designs
                                    annually.
                                </li>
                                <li>
                                    Photography Costs: Approx. 20,000-70,000 KRW per garment
                                    <br />
                                    (Based on traditional methods using models and studios).
                                </li>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 시장의 문제점 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    시장의 문제점
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 6 }}>
                    Market Problems
                </Typography>

                <Grid container spacing={6} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box
                            component="img"
                            src="https://d1vb419ubgm46b.cloudfront.net/PRD/202601/4983c64c88e32b1529dc50af61e0199b.webp"
                            alt="Market Problems"
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 9 }}>
                        <Box
                            component="ul"
                            sx={{ pl: 3, '& li': { mb: 3, color: '#333', lineHeight: 1.9, fontSize: '18px' } }}
                        >
                            <li>
                                <strong>월평균 촬영 비용</strong>
                                <br />
                                - 소규모: 월 약 150만 원 ~ 300만 원 (신상 20~40종 기준)
                                <br />- 중대형: 월 약 500만 원 ~ 1,000만 원 이상 (전문 모델 및 스튜디오 정기 대관)
                            </li>
                            <li>
                                <strong>현재 촬영 방식 및 소요 시간</strong>
                                <br />
                                - 촬영 장소: 스튜디오
                                <br />
                                - 준비 단계: 모델 및 스튜디오 예약 (최소 3일 ~ 1주일 전)
                                <br />
                                &nbsp;&nbsp;1) 촬영 당일: 신상 10~15종 기준 약 4시간 ~ 6시간
                                <br />
                                &nbsp;&nbsp;2) 후보정 작업: 촬영 후 최종 결과물 3일 ~ 5일 소요
                            </li>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box
                            component="ul"
                            sx={{ pl: 3, '& li': { mb: 2, color: '#666', lineHeight: 1.8, fontSize: '16px' } }}
                        >
                            <li>
                                Average Monthly Photography Costs
                                <br />
                                - Small-scale: Approx. 1.5 million – 3 million KRW/month (Based on 20-40 new items).
                                <br />- Medium-to-Large: Approx. 5 million – over 10 million KRW/month (Regular rentals
                                of professional models and studios).
                            </li>
                            <li>
                                Current Photography Method & Time Required
                                <br />
                                - Location: Studio
                                <br />
                                - Preparation: Booking models and studios (Minimum 3 days to 1 week in advance).
                                <br />
                                &nbsp;&nbsp;1) Shoot Day: Approx. 4-6 hours for 10-15 new items.
                                <br />
                                &nbsp;&nbsp;2) Post-processing: 3-5 days for final results after shooting.
                                <br />
                                &nbsp;&nbsp;3) Total: Minimum 5 to 10 days required before picture updates.
                            </li>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

// Business 페이지 컴포넌트
const BusinessSection = () => {
    return (
        <Box sx={{ flex: 1 }}>
            {/* 히어로 배너 */}
            <Box
                sx={{
                    position: 'relative',
                    height: '300px',
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/6539430dd5852f02f812cf55492a14a0.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            ></Box>

            {/* 삽쓰리(SHOP3) 소개 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    삽쓰리(SHOP3) 무엇을 하는 서비스인가?
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 6 }}>
                    What is SHOP3?
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ color: '#333', lineHeight: 2, mb: 4, fontSize: '18px' }}>
                        삽쓰리(SHOP3)는 최신 생성형 인공지능(AI) 기술을 기반으로 이커머스 및 소셜 미디어에서 활용 가능한
                        AI 기반 의류 이미지 생성 및 모델 피팅 서비스를 제공합니다.
                        <br />
                        사용자는 단일 이미지 또는 제품 사진만으로도 실제 촬영한 것과 유사한 고품질 이미지 컷을
                        자동생성할 수 있으며, 다양한 스타일·배경·모델 조건을 조합해 이미지 콘텐츠를 빠르게 대량 생산할
                        수 있는 서비스입니다.
                    </Typography>

                    <Typography sx={{ color: '#666', lineHeight: 2, fontStyle: 'italic', fontSize: '16px' }}>
                        SHOP3 is a service that provides AI-based clothing image generation and model fitting services
                        for e-commerce and social media, based on the latest generative AI technology. Users can
                        automatically generate high-quality image cuts similar to actual photos using only a single
                        image or product photo. It is a service that allows rapid mass production of image content by
                        combining various styles, backgrounds, and model conditions.
                    </Typography>
                </Box>
            </Container>

            {/* 기능 섹션들 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    {/* 첫 번째 기능: AI 모델 파이프라인 */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/99ee877e48ca12665cbe20424bf18f36.webp"
                                alt="AI Model Pipeline"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography
                                sx={{ fontWeight: 700, color: '#333', fontSize: '20px', mb: 2, lineHeight: 1.6 }}
                            >
                                구글 및 주요 AI 모델 (예: Gemini, 나노바나나, Whisk, VEO3 등) 기반 이미지 생성/편집
                                파이프라인 적용
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '16px', lineHeight: 1.8 }}>
                                Technology: Applies image generation/editing pipelines based on Google and major AI
                                models (e.g., Gemini, Nano Banana, Whisk, VEO3, etc.).
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* 두 번째 기능: 오케스트레이션 */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/f2bdb689e831c72c796b9ecb085a07ea.webp"
                                alt="Orchestration"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography
                                sx={{ fontWeight: 700, color: '#333', fontSize: '20px', mb: 2, lineHeight: 1.6 }}
                            >
                                여러 AI 공정을 정교하게 조율하는 오케스트레이션 방식으로 자연스러운 고품질의 의류 관련
                                결과물을 생산
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '16px', lineHeight: 1.8 }}>
                                Orchestration: Produces natural, high-quality clothing-related results through an
                                orchestration method that precisely coordinates multiple AI processes.
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* 세 번째 기능: 사용 사례 */}
                    <Grid container spacing={6} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/af48470fffca126c4c95908215286584.webp"
                                alt="Use Cases"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography
                                sx={{ fontWeight: 700, color: '#333', fontSize: '20px', mb: 2, lineHeight: 1.6 }}
                            >
                                사용 사례: 제품 상세 이미지, 스타일 컷, SNS 광고 이미지, 쇼핑몰 피팅 컷 등
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '16px', lineHeight: 1.8 }}>
                                Use Cases: Product detail images, style cuts, SNS ad images, shopping mall fitting cuts,
                                etc.
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

// Problem 페이지 컴포넌트
const ProblemSection = () => {
    return (
        <Box sx={{ flex: 1 }}>
            {/* 히어로 배너 */}
            <Box
                sx={{
                    position: 'relative',
                    height: '300px',
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/7d661d7d4f8b4648010c460530294bf6.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            ></Box>

            {/* 메인 타이틀 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    SHOP3가 해결하는 시장의 문제
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 4 }}>
                    Problems SHOP3 Solves
                </Typography>

                {/* 서브타이틀 */}
                <Box sx={{ borderLeft: '4px solid #FB6D48', pl: 2, mb: 6 }}>
                    <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '18px' }}>
                        타겟 의류시장의 구조적 문제
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '16px' }}>
                        Structural Problems of the Target Clothing Market
                    </Typography>
                </Box>

                {/* (1) 이커머스·패션 산업의 이미지 제작방식에 대한 혁신이 필요 */}
                <Box sx={{ mb: 8 }}>
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (1) 이커머스·패션 산업의 이미지 제작방식에 대한 혁신이 필요
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>
                        Need for Innovation in Image Production for E-commerce & Fashion Industry
                    </Typography>

                    <Grid container spacing={6} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/41a38427ab96cd686b00d4f1d932e3d0.webp"
                                alt="Fashion Industry"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Typography sx={{ color: '#333', lineHeight: 1.9, fontSize: '16px', mb: 3 }}>
                                패션 및 이커머스 산업에서 상품 이미지는 구매 결정의 핵심 요소로 중요하지만, 현재까지도
                                대부분의 상품 이미지는 전통적인 촬영과 편집 방식에 의존하고 있습니다.
                            </Typography>
                            <Typography
                                sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', mb: 3, fontStyle: 'italic' }}
                            >
                                While product images are key factors in purchase decisions, most are still dependent on
                                traditional photography and editing methods.
                            </Typography>

                            <Box
                                component="ul"
                                sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                            >
                                <li>
                                    <strong>모델 섭외, 스튜디오 촬영, 조명·세트 구성</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Recruiting models, studio shooting, lighting/set construction.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>촬영 후 편집 및 보정</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Post-shoot editing and retouching.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>컬러·사이즈·착용 컷별 반복 촬영</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Repetitive shooting for each color, size, and wearing cut.
                                    </Typography>
                                </li>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                    이 구조는 품질은 확보할 수 있으나 높은 비용과 소요되는 많은 시간으로 인해, 확장성이
                                    극도로 낮고, 빠른 상품회전과 대량계가 필요한 현재의 요구에 맞지 않습니다.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.8,
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        mt: 1,
                                    }}
                                >
                                    This structure secures quality but has extremely low scalability due to high costs
                                    and time consumption, failing to meet current demands for rapid product.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            {/* (2) 고품질 생성형 AI 확산에도 불구한 '판매 가능한 이미지'의 구조적 한계 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (2) 고품질 생성형 AI 확산에도 불구한 '판매 가능한 이미지'의 구조적 한계
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>
                        Structural Limitations of "Sale-Ready Images" Despite the Proliferation of High-Quality
                        Generative AI
                    </Typography>

                    <Grid container spacing={6} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box
                                    component="img"
                                    src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/28c0d31e0de4b55622f630f1ded8c24f.webp"
                                    alt="AI Limitations 1"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Box
                                    component="img"
                                    src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/56101384f53c78a425b68c17e7f84d62.webp"
                                    alt="AI Limitations 2"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography sx={{ color: '#333', lineHeight: 1.9, fontSize: '16px', mb: 3 }}>
                                SHOP3가 독점적 계약을 확보하고 있는 한국 패션의 메카인 동대문의 수많은 의류 분야
                                종사자가 사용하기에는 아직도 다음과 같은 한계가 존재합니다.
                            </Typography>
                            <Typography
                                sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', mb: 4, fontStyle: 'italic' }}
                            >
                                Despite SHOP3 having secured exclusive partnerships, there are still the following
                                limitations for the many apparel industry professionals in Dongdaemun—Korea's fashion
                                hub—to effectively use these solutions.
                            </Typography>

                            <Box
                                component="ul"
                                sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                            >
                                <li>
                                    <strong>인체 비율 왜곡, 부자연스러운 착용감</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Distortion of body proportions, unnatural fit.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>실제 상품과 다른 색상·질감 표현</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Color and texture representation differing from actual products.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>브랜드 신뢰도를 해치는 인공 아티팩트(Artifact)</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Random artifacts that damage brand reliability.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>채널 상품 간 이미지 톤 불일치</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Inconsistency in image tone across channels and products.
                                    </Typography>
                                </li>
                            </Box>

                            <Box
                                sx={{
                                    mt: 4,
                                    p: 3,
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                }}
                            >
                                <Typography sx={{ color: '#333', lineHeight: 1.9, fontSize: '15px', mb: 2 }}>
                                    이러한 문제는 단순한 시각적 완성도의 문제가 아니라 구매 신뢰도, 반품률, 브랜드
                                    이미지에 직접적인 영향을 미치는 상업적 리스크로 결과적으로 많은 의류판매자와
                                    보세브랜드들은 AI 기술의 가능성을 알면서도 아직 적극적으로 활용할 방법을 못찾고
                                    있습니다.
                                </Typography>
                                <Typography
                                    sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', fontStyle: 'italic' }}
                                >
                                    These are not just visual issues but commercial risks affecting trust, return rates,
                                    and brand image. Consequently, many sellers find it difficult to actively utilize AI
                                    technology despite knowing its potential.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* (3) 소상공인·중소 셀러에게 불리한 경쟁 구조 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                    (3) 소상공인·중소 셀러에게 불리한 경쟁 구조
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>
                    Disadvantageous Competition Structure for Small Business Owners
                </Typography>

                <Grid container spacing={6} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                            component="img"
                            src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/087ee89d9b2f874e1096d323765d9fbd.webp"
                            alt="Small Business"
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 4 }}>
                            <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '16px', mb: 2 }}>
                                패션업계에서 고품질 제품 이미지제작은 필수적이지만, 다음의 문제를 가지고 있습니다.
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                                In the fashion industry, producing high-quality product images is essential; however, it
                                comes with the following challenges.
                            </Typography>
                        </Box>

                        <Box
                            component="ul"
                            sx={{ pl: 2, '& li': { mb: 3, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                        >
                            <li>
                                <strong>대형 브랜드: 전담 인력/스튜디오/예산 보유</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Large Brands: Possess dedicated personnel, studios, and budgets.
                                </Typography>
                            </li>
                            <li>
                                <strong>소상공인·중소 셀러: 제한된 자원, 높은 외주 비용 부담</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Small Owners/Sellers: Limited resources, burden of high outsourcing costs.
                                </Typography>
                            </li>
                        </Box>

                        <Box
                            sx={{ mt: 4, p: 3, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}
                        >
                            <Typography sx={{ color: '#333', lineHeight: 1.9, fontSize: '15px', mb: 2 }}>
                                이로 인해 제품 경쟁력과 무관하게 소상공인, 중소 셀러의 입장에서는 이미지 제작 능력이
                                시장 진입 장벽으로 작용하고 있으며, AI 기술조차 일부에게만 유리하게 작동될 위험이
                                존재합니다.
                            </Typography>
                            <Typography sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', fontStyle: 'italic' }}>
                                Image production capability acts as a market entry barrier regardless of product
                                competitiveness, and there is a risk that AI technology may only benefit a select few.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* (4) 인플루언서 커머스·글로벌 이커머스 확산에 맞는 대량 콘텐츠 생산 방식의 부재 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (4) 인플루언서 커머스·글로벌 이커머스 확산에 맞는 대량 콘텐츠 생산 방식의 부재
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>
                        Lack of Mass Content Production Methods for Influencer/Global Commerce
                    </Typography>

                    <Box sx={{ p: 3, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0', mb: 4 }}>
                        <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '16px', mb: 2 }}>
                            현재의 온라인 커머스 환경에서는 다음과 같은 사항들이 요구됩니다.
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic', mb: 3 }}>
                            Current online commerce requires:
                        </Typography>

                        <Box
                            component="ul"
                            sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                        >
                            <li>
                                <strong>많은 상품을 동시에 다양한 콘셉트/채널/시장에 맞게 빠르게 변환</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Rapid conversion of many products for various concepts/channels/markets.
                                </Typography>
                            </li>
                            <li>
                                <strong>짧은 주기로 반복 생산 가능한 콘텐츠 구조</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Content structures capable of repetitive production in short cycles.
                                </Typography>
                            </li>
                            <li>
                                <strong>촬영에 의존하지 않는 유연한 콘텐츠 파이프라인</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Flexible content pipelines not reliant on physical shooting.
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box sx={{ p: 3, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                        <Typography sx={{ color: '#333', lineHeight: 1.9, fontSize: '16px', fontWeight: 500 }}>
                            그러나 기존 방식은 이러한 요구를 충족하지 못하고 있습니다.
                        </Typography>
                        <Typography
                            sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', fontStyle: 'italic', mt: 1 }}
                        >
                            Existing methods fail to meet these requirements.
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

// Solution 페이지 컴포넌트
const SolutionSection = () => {
    return (
        <Box sx={{ flex: 1 }}>
            {/* 히어로 배너 */}
            <Box
                sx={{
                    position: 'relative',
                    height: '300px',
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/f9f03b9bc99e3f56799d2043c2937021.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            ></Box>

            {/* 메인 타이틀 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    SHOP3의 해결방법
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 4 }}>
                    SHOP3's Solution
                </Typography>

                <Box sx={{ mb: 6 }}>
                    <Typography sx={{ color: '#333', lineHeight: 2, fontSize: '18px', mb: 3 }}>
                        SHOP3는 단순한 AI 이미지 생성 도구가 아니라, 이커머스 실사용 환경을 기준으로 생성형 AI 기술을
                        재구성한 이미지 생산 플랫폼입니다.
                    </Typography>
                    <Typography sx={{ color: '#666', lineHeight: 2, fontSize: '16px', fontStyle: 'italic' }}>
                        SHOP3 is not just an AI image generation tool but an image production platform that reconstructs
                        generative AI technology based on real-world e-commerce environments.
                    </Typography>
                </Box>

                {/* (1) 기술적 해결 방식 */}
                <Box sx={{ mb: 8 }}>
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (1) 기술적 해결 방식
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>Technical Approach</Typography>

                    <Grid container spacing={6} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/27d69055ebd04d8c97791e2befb6e53c.webp"
                                alt="Technical Approach"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                                <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                    SHOP3는 "이미지를 생성하는 AI"가 아니라 "판매 가능한 결과를 안정적으로 생산하는 AI
                                    파이프라인"을 목표로 설계되었습니다.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.8,
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        mt: 1,
                                    }}
                                >
                                    Designed not as "AI that generates images" but as an "AI pipeline that stably
                                    produces sellable results."
                                </Typography>
                            </Box>

                            <Box
                                component="ul"
                                sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                            >
                                <li>
                                    <strong>단체/의류/배경등을 따로 인식하는 구조로 피팅 결과의 자연스러움 확보</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Natural Fit: Ensures natural results by separating recognition of body,
                                        clothing, and backgrounds.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>제작목적과 사용자 요구에 따라 최적의 생성형 AI툴을 조합</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Optimal Combination: Combines optimal generative AI tools according to
                                        production purpose and user needs.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>원본 상품 이미지 기반 색상 질감 보존 우선 설계</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Preservation: Prioritizes preservation of color and texture based on original
                                        product images.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>브랜드·셀러 단위로 일관성을 유지할 수 있는 이미지 생성 구조</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Consistency: Image generation structure maintaining consistency at the
                                        brand/seller level.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>상업적 사용을 전제로 한 품질 기준 및 결과물 제어</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Control: Quality standards and result control premised on commercial use.
                                    </Typography>
                                </li>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                                <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                    이를 통해 AI 결과물의 예측가능성과 신뢰성을 높이고, 실제 이커머스 환경에서 사용
                                    가능한 품질 수준을 확보하였습니다.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.8,
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        mt: 1,
                                    }}
                                >
                                    This increases the predictability and reliability of AI results, securing a quality
                                    level usable in actual e-commerce environments.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            {/* (2) 산업적 해결 방식 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (2) 산업적 해결 방식
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>Industry-Oriented Solution</Typography>

                    <Grid container spacing={6} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box
                                component="img"
                                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/f84124c489e0a6e713c2c9a5aecc5783.webp"
                                alt="Industry Solution"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box
                                sx={{
                                    p: 3,
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                    mb: 3,
                                }}
                            >
                                <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                    SHOP3는 이커머스 운영 구조 자체를 개선하는 데 초점을 둡니다.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.8,
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        mt: 1,
                                    }}
                                >
                                    Focuses on improving the e-commerce operation structure itself.
                                </Typography>
                            </Box>

                            <Box
                                component="ul"
                                sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                            >
                                <li>
                                    <strong>촬영 중심의 비용 구조를 AI 기반 반복 생산 구조로 전환</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Transition from shooting-centric cost structures to AI-based repetitive
                                        production structures.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>신상품 출시, 테스트 판매, 마케팅용 콘텐츠 제작의 속도 향상</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Increased speed for new product launches, test sales, and marketing content
                                        creation.
                                    </Typography>
                                </li>
                                <li>
                                    <strong>소량 다품종·빠른 회전 구조에 적합한 이미지 생산 방식 제공</strong>
                                    <br />
                                    <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                        Image production methods suitable for small-quantity, high-variety, and
                                        fast-turnover structures.
                                    </Typography>
                                </li>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                    이는 단순한 비용 절감이 아니라, 동 분야의 이커머스 운영 전반의 생산성을 높이는데
                                    기여합니다.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.8,
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        mt: 1,
                                    }}
                                >
                                    This is not merely a cost reduction, but a contribution to improving overall
                                    productivity across e-commerce operations in the fashion industry.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* (3) 사회적 해결 방식 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                    (3) 사회적 해결 방식
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>Social Impact</Typography>

                <Grid container spacing={6} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                            component="img"
                            src="https://d1vb419ubgm46b.cloudfront.net/DEV/202601/e8d20a71e978d36105df8dad76462a28.webp"
                            alt="Social Impact"
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                            <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                SHOP3는 생성형 AI 기술의 혜택을 대기업 중심이 아닌 소상공인과 중소 셀러까지 확장하는 데
                                가치를 둡니다.
                            </Typography>
                            <Typography
                                sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', fontStyle: 'italic', mt: 1 }}
                            >
                                Values extending the benefits of generative AI technology to small business owners, not
                                just large corporations.
                            </Typography>
                        </Box>

                        <Box
                            component="ul"
                            sx={{ pl: 2, '& li': { mb: 2, color: '#333', lineHeight: 1.8, fontSize: '15px' } }}
                        >
                            <li>
                                <strong>고비용 촬영 인프라 없이도, 고품질 이미지 제작 가능</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    High-quality image production without expensive infrastructure.
                                </Typography>
                            </li>
                            <li>
                                <strong>AI 사용에 전문지식과 경험이 없어도 사용가능한 UX 및 서비스 형태 제공</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    UX/Service accessible without specialized AI knowledge or experience.
                                </Typography>
                            </li>
                            <li>
                                <strong>이미지 품질 격차로 인한 시장 불균형 완화</strong>
                                <br />
                                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>
                                    Alleviation of market imbalances caused by image quality gaps.
                                </Typography>
                            </li>
                        </Box>

                        <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                            <Typography sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px' }}>
                                이는 Google의 AI 기술을 통해 더 많은 사업자가 공정하게 경쟁할 수 있는 환경을 조성하는
                                사회적 가치로 이어집니다.
                            </Typography>
                            <Typography
                                sx={{ color: '#666', lineHeight: 1.8, fontSize: '14px', fontStyle: 'italic', mt: 1 }}
                            >
                                This leads to the social value of creating an environment where more businesses can
                                compete fairly through Google's AI technology.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* (4) 시장의 문제와 SHOP3의 해결 방식 요약 */}
            <Box sx={{ backgroundColor: '#f9f9f9', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                        (4) 시장의 문제와 SHOP3의 해결 방식 요약
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>
                        Summary of Market Problems and SHOP3 Solutions
                    </Typography>

                    {/* 비교 테이블 */}
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 0,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            {/* 헤더 */}
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '14px' }}>
                                    시장의 문제
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '12px' }}>Market Problem</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '14px' }}>
                                    기존 서비스구조
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '12px' }}>
                                    Existing Service Structure
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: '#FB6D48', borderBottom: '1px solid #e0e0e0' }}>
                                <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '14px' }}>
                                    SHOP3의 서비스
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                                    SHOP3 Service
                                </Typography>
                            </Box>

                            {/* Row 1 */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                                    이미지 제작비용
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>Image Production Cost</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ color: '#333', fontSize: '13px' }}>고비용</Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>High Cost</Typography>
                            </Box>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8f6' }}>
                                <Typography sx={{ color: '#FB6D48', fontSize: '13px', fontWeight: 500 }}>
                                    AI 기반 반복 생산 구조로 비용 절감
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Cost reduction via AI-based repetitive generation structure
                                </Typography>
                            </Box>

                            {/* Row 2 */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                                    제작 속도
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>Production Speed</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ color: '#333', fontSize: '13px' }}>
                                    상품별/안면 재촬영 필요
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Re-shooting required per product/cut
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8f6' }}>
                                <Typography sx={{ color: '#FB6D48', fontSize: '13px', fontWeight: 500 }}>
                                    단일 이미지기반 다중 콘텐츠 생성
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Multi-content generation based on a single image
                                </Typography>
                            </Box>

                            {/* Row 3 */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                                    AI피팅 신뢰성
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>AI Fitting Reliability</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ color: '#333', fontSize: '13px' }}>
                                    AI 결과물 비자연적/왜곡
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Distorted/Unnatural results
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8f6' }}>
                                <Typography sx={{ color: '#FB6D48', fontSize: '13px', fontWeight: 500 }}>
                                    상업용 기준 피팅+스타일컷 자동 달성
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Natural fitting design based on commercial standards
                                </Typography>
                            </Box>

                            {/* Row 4 */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                                    색상/질감 정확도
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>Color/Texture Accuracy</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: { md: '1px solid #e0e0e0' },
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography sx={{ color: '#333', fontSize: '13px' }}>
                                    보정변질, AI색상 드리프트
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Retouching deviation, AI color drift
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8f6' }}>
                                <Typography sx={{ color: '#FB6D48', fontSize: '13px', fontWeight: 500 }}>
                                    원본 기존 색상/질감 보존 우선
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Priority on preserving original color/texture
                                </Typography>
                            </Box>

                            {/* Row 5 */}
                            <Box sx={{ p: 2, borderRight: { md: '1px solid #e0e0e0' }, backgroundColor: '#fff' }}>
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                                    접근성
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>Accessibility</Typography>
                            </Box>
                            <Box sx={{ p: 2, borderRight: { md: '1px solid #e0e0e0' }, backgroundColor: '#fff' }}>
                                <Typography sx={{ color: '#333', fontSize: '13px' }}>높은 진입 비용</Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>High entry costs</Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: '#fff8f6' }}>
                                <Typography sx={{ color: '#FB6D48', fontSize: '13px', fontWeight: 500 }}>
                                    저비용/저진입 AI 서비스모델
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: '11px' }}>
                                    Low-cost/Low-barrier AI service
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* (5) 종합 요약 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>(5) 종합 요약</Typography>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>Comprehensive Summary</Typography>

                <Box sx={{ p: 4, backgroundColor: '#f5f5f5', borderRadius: 2, borderLeft: '4px solid #FB6D48' }}>
                    <Typography sx={{ color: '#333', lineHeight: 2, fontSize: '16px', mb: 3 }}>
                        SHOP3는 생성형 AI 기술을 활용해 이커머스·패션산업 업계의 비효율적인 이미지 제작 구조를
                        근본적으로 개선하고, 제작비용 소상공인과 중소 셀러에게도 실질적인 활용 사례를 제공합니다.
                    </Typography>
                    <Typography sx={{ color: '#333', lineHeight: 2, fontSize: '16px', mb: 3 }}>
                        이는 Google의 생성형 AI 가술이 (1)실제 산업 문제 해결로 이어지고, (2) 더 넓은 사용자층에게
                        AI기술 활용 기회를 제공하며 (3)책임 있고 정당적인 방식으로 활용될 수 있음을 입증하는데 기여할
                        것입니다.
                    </Typography>
                    <Typography sx={{ color: '#666', lineHeight: 2, fontSize: '14px', fontStyle: 'italic' }}>
                        SHOP3 fundamentally improves the inefficient image production structure of the e-commerce and
                        fashion industries using generative AI technology and provides practical use cases that extend
                        these benefits to small business owners. This contributes to proving that Google's generative AI
                        technology can (1) lead to solutions for actual industrial problems, (2) provide opportunities
                        for AI technology use to a wider user base, and (3) be utilized in a responsible and realistic
                        manner.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

// Customer 페이지 컴포넌트
const CustomerSection = () => {
    // 고객 유형 데이터
    const customerTypes = [
        {
            id: 1,
            title: '도소매업자',
            subtitle: 'Wholesalers/Retailer',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/03fffe17773338ece624559e2988fe91.webp',
            needsKr: '많은 제품 이미지 필요, 촬영 비용 부담',
            needsEn: 'Need many product images, burdened by shooting costs',
            effectsKr: '이미지 콘텐츠 대량 생성으로 비용·시간 절약',
            effectsEn: 'Save cost/time via mass production of image content',
        },
        {
            id: 2,
            title: '브랜드 제조사',
            subtitle: 'Brand Manufacturers',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/58dff9cae32d70004f03289ee7956713.webp',
            needsKr: '글로벌·SNS용 콘텐츠 다양화 필요',
            needsEn: 'Need content diversification for Global/SNS',
            effectsKr: '브랜드 비주얼 일관성 확보',
            effectsEn: 'Secure brand visual consistency',
        },
        {
            id: 3,
            title: '인플루언서',
            subtitle: 'Influencers',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/20c6f6752dd220694de2274a393d7222.webp',
            needsKr: '다양한 룩북·스타일 이미지 필요',
            needsEn: 'Need various lookbook/style images',
            effectsKr: '빠른 콘텐츠 생산 및 마케팅 확장',
            effectsEn: 'Fast content production and marketing expansion',
        },
        {
            id: 4,
            title: '이커머스 운영자',
            subtitle: 'E-commerce Operators',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/ae7ea79a8ceb154ec2136de420ebb7b7.webp',
            needsKr: '상세페이지 이미지 강화 필요',
            needsEn: 'Need to strengthen detail page images',
            effectsKr: '전환율 상승 & 상품 페이지 경쟁력 강화',
            effectsEn: 'Increase conversion rates & strengthen page competitiveness',
        },
        {
            id: 5,
            title: '글로벌 구매자 대상 판매자',
            subtitle: 'Global Sellers',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/86a2542f9d3f49ccd514c2570052497c.webp',
            needsKr: '다양한 언어·시장 컨텍스트 맞춤 이미지 필요',
            needsEn: 'Need images tailored to various languages/market contexts',
            effectsKr: '글로벌 콘텐츠 로컬라이제이션 지원',
            effectsEn: 'Support for global content localization',
        },
    ];

    return (
        <Box sx={{ flex: 1 }}>
            {/* 히어로 배너 */}
            <Box
                sx={{
                    position: 'relative',
                    height: '300px',
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/ed4dbc3f97a481d88b69517a6c28172f.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            ></Box>

            {/* 메인 타이틀 섹션 */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                    타겟 고객 정보
                </Typography>
                <Typography variant="h5" sx={{ color: '#666', mb: 6 }}>
                    Target Customer Information
                </Typography>

                {/* 고객 유형 리스트 */}
                {customerTypes.map((customer, index) => (
                    <Box
                        key={customer.id}
                        sx={{
                            mb: index < customerTypes.length - 1 ? 8 : 0,
                            pb: index < customerTypes.length - 1 ? 8 : 0,
                            borderBottom: index < customerTypes.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '18px', mb: 1 }}>
                            ({customer.id}) {customer.title}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>{customer.subtitle}</Typography>

                        <Grid container spacing={6} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box
                                    component="img"
                                    src={customer.image}
                                    alt={customer.title}
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 8 }}>
                                {/* 필요·문제점 */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '15px', mb: 2 }}>
                                        • 필요 · 문제점 (Needs & Problems)
                                    </Typography>
                                    <Typography sx={{ color: '#333', fontSize: '16px', fontWeight: 500, mb: 1 }}>
                                        {customer.needsKr}
                                    </Typography>
                                    <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                                        {customer.needsEn}
                                    </Typography>
                                </Box>

                                {/* 기대 효과 */}
                                <Box
                                    sx={{
                                        p: 3,
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 1,
                                        borderLeft: '3px solid #FB6D48',
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '15px', mb: 2 }}>
                                        • 기대 효과 (Expected Effects)
                                    </Typography>
                                    <Typography sx={{ color: '#FB6D48', fontSize: '16px', fontWeight: 600, mb: 1 }}>
                                        {customer.effectsKr}
                                    </Typography>
                                    <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                                        {customer.effectsEn}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                {/* 글로벌 서비스 안내 */}
                <Box
                    sx={{
                        mt: 6,
                        p: 3,
                        backgroundColor: '#fff3e0',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                    }}
                >
                    <Typography sx={{ color: '#FB6D48', fontSize: '16px' }}>※</Typography>
                    <Box>
                        <Typography sx={{ color: '#333', fontSize: '15px', fontWeight: 500 }}>
                            SHOP3 는 중국, 베트남 등 글로벌 용 서비스를 구축하고 있습니다.
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                            SHOP3 is building global services for China, Vietnam, etc.
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

// Product 페이지 섹션
const ProductSection = () => {
    const features = [
        {
            id: 1,
            titleKr: '세팅샷',
            titleEn: 'Styled Shot',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/913ca0d244b2c1effd6d6e9fffb76c09.webp',
        },
        {
            id: 2,
            titleKr: '감성샷',
            titleEn: 'Mood Shot',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/8174943cfd07b0916a8dc493a6768270.webp',
        },
        {
            id: 3,
            titleKr: '옷걸이샷',
            titleEn: 'Hanger Shot',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/8c449835737199726b7c92e46746f75c.webp',
        },
        {
            id: 4,
            titleKr: '디테일샷',
            titleEn: 'Detail Shot',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/d08540812c81e0e6248559dc57077317.webp',
        },
        {
            id: 5,
            titleKr: '모델샷',
            titleEn: 'Model Shot',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/8a0932934f9618a5715d7105c02a7548.webp',
        },
    ];

    const youtubeVideos = ['https://www.youtube.com/embed/ZJySWI1fMAo', 'https://www.youtube.com/embed/B1YRmGSKjSY'];

    return (
        <Box sx={{ flex: 1 }}>
            {/* Hero Banner */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: '200px', md: '300px' },
                    backgroundImage:
                        'url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/babf4da27c098c4a4b42bb3b17faeb66.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    },
                }}
            ></Box>

            {/* SHOP3 Seller 소개 */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                        샵쓰리 셀러
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: '#555', mb: 3, fontSize: { xs: '1.1rem', md: '1.3rem' } }}
                    >
                        SHOP3 Seller
                    </Typography>
                    <Typography sx={{ color: '#333', fontSize: '16px', lineHeight: 1.8, mb: 2 }}>
                        도매상점인 셀러들이 판매하는 의류는 피팅 사진이라는 일상, 옷의 특징을 그대로 보여주는 누끼샷,
                        옷걸이 샷, 감성 샷, 세팅 샷의 피팅 사진을 선호합니다. 샵쓰리는 도매 피팅 솔루션만 이러한
                        사진들을 만들기 위해 가장 특화된 AI 피팅 솔루션입니다.
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', lineHeight: 1.8, fontStyle: 'italic' }}>
                        Unlike general fitting photos, the clothing sold by wholesale sellers prefers fitting photos
                        that show the characteristics of the clothes as they are, such as ghost mannequin cuts, hanger
                        cuts, emotional mood cuts, and setting cuts. The SHOP3 wholesale fitting solution is the most
                        specialized AI fitting solution for creating these types of shots.
                    </Typography>
                </Box>

                {/* 기능 목록 */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        기능 목록
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 4 }}>Feature List</Typography>
                </Box>

                {/* 각 피팅샷 섹션 */}
                {features.map((feature) => (
                    <Box key={feature.id} sx={{ mb: 8, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            ({feature.id}) {feature.titleKr}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '14px', mb: 3 }}>{feature.titleEn}</Typography>
                        <Box
                            component="img"
                            src={feature.image}
                            alt={feature.titleKr}
                            sx={{
                                width: '100%',
                                maxWidth: '600px',
                                height: 'auto',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}
                        />
                    </Box>
                ))}

                {/* SHOP3 AI Studio 섹션 - YouTube 영상 */}
                <Box sx={{ mb: 8, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        (6) SHOP3 AI Studio
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '14px', mb: 3 }}>AI Studio Demo Videos</Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        {youtubeVideos.map((videoUrl, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: '800px',
                                    paddingTop: '45%',
                                    maxHeight: '450px',
                                }}
                            >
                                <iframe
                                    src={videoUrl}
                                    title={`SHOP3 AI Studio Demo ${index + 1}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        maxWidth: '800px',
                                        maxHeight: '450px',
                                        border: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

// Team 페이지 섹션
const TeamSection = () => {
    const teamMembers = [
        {
            id: 1,
            role: 'CEO',
            name: 'PETER OH SEG HWAN',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/fb63e8b0f40851bd7baab3bc1fa71d17.webp',
            descriptions: [
                'De La salle University Psychology',
                'first founder educational call center in the world',
                'Fashion AI Fitting Solution',
            ],
            linkedin: 'https://www.linkedin.com/in/peter-oh-700b33379/',
        },
        {
            id: 2,
            role: 'Project Development Manager',
            name: 'KIM MIN JIN',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/9ec360badf0a7c919d156e593d3cd38f.webp',
            descriptions: [
                'Cross-platform development with GetX state management',
                'Real-time image processing, editing, and AI-based fitting image generation',
                'Payment integration and real-time notifications',
                'WebView integration',
                'Backend API Development (NestJS)',
                'Microservices-based RESTful API architecture',
                'Google Gemini Flash Pro 2.5 prompt optimization',
                'AWS S3, real-time chat, and image/video processing',
            ],
            linkedin: 'https://www.linkedin.com/in/mjkim0724/',
        },
        {
            id: 3,
            role: 'Tech Lead & Backend Developer',
            name: 'SON YOO HAN',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/ef6e2ca81249b50662764052c483ed00.webp',
            descriptions: [
                'responsible for AI model integration and system architecture.',
                'Expertise in diffusion-based image generation, custom model fine-tuning, and AI-powered virtual try-on technology.',
            ],
            linkedin: 'https://www.linkedin.com/in/yoohan-son-b204463a5',
        },
        {
            id: 4,
            role: 'Software Architect',
            name: 'MIN CHEOL HONG',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/256fb3c52a8cff7055455f252848de64.webp',
            descriptions: [
                'responsible for designing and building scalable systems.',
                'Expertise in microservices, event-driven architecture, and cloud-native infrastructure for high-traffic, reliable solutions.',
            ],
            linkedin: 'https://www.linkedin.com/in/mch00/',
        },
        {
            id: 5,
            role: 'UI/UX Designer (App & Web)',
            name: 'HAN SEUNG YEON',
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202601/c2c06fc3f925db52439612a81d1d87bf.webp',
            descriptions: ['UI/UX Design for Mobile Apps and Web'],
            linkedin: 'https://www.linkedin.com/in/flowingsy/',
        },
    ];

    return (
        <Box sx={{ flex: 1 }}>
            {/* Hero Banner */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: '200px', md: '300px' },
                    backgroundImage:
                        'url(https://d1vb419ubgm46b.cloudfront.net/DEV/202601/600b5013809343f901e0ea13b4725afe.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    },
                }}
            ></Box>

            {/* 팀 소개 */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                        샵쓰리 팀
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: '#555', fontSize: { xs: '1.1rem', md: '1.3rem' } }}
                    >
                        SHOP3 Team
                    </Typography>
                </Box>

                {/* 팀 멤버 목록 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {teamMembers.map((member) => (
                        <Box
                            key={member.id}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 4,
                                alignItems: { xs: 'center', md: 'flex-start' },
                            }}
                        >
                            {/* 프로필 이미지 */}
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Box
                                    component="img"
                                    src={member.image}
                                    alt={member.name}
                                    sx={{
                                        width: { xs: '180px', md: '200px' },
                                        height: { xs: '180px', md: '200px' },
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                    }}
                                />
                                {/* LinkedIn 아이콘 */}
                                <Box
                                    component="a"
                                    href={member.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        position: 'absolute',
                                        bottom: '-12px',
                                        right: '-12px',
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#0077B5',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '18px',
                                        textDecoration: 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            backgroundColor: '#005885',
                                        },
                                    }}
                                >
                                    <LinkedInIcon sx={{ fontSize: '24px' }} />
                                </Box>
                            </Box>

                            {/* 멤버 정보 */}
                            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        color: '#333',
                                        mb: 0.5,
                                    }}
                                >
                                    {member.role}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        color: '#000',
                                        mb: 2,
                                    }}
                                >
                                    {member.name}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {member.descriptions.map((desc, idx) => (
                                        <Typography
                                            key={idx}
                                            sx={{
                                                fontSize: '14px',
                                                color: '#555',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {desc}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

// 페이지 플레이스홀더 컴포넌트
const PagePlaceholder = ({ pageName }: { pageName: string }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                backgroundColor: '#f9f9f9',
            }}
        >
            <Typography
                variant="h2"
                sx={{
                    color: '#333',
                    fontWeight: 600,
                    textAlign: 'center',
                }}
            >
                {pageName}
            </Typography>
        </Box>
    );
};

// 메인 페이지 컴포넌트
const Main2 = () => {
    const { isOpen: isVendorLoginOpen, setIsOpen: setVendorLoginOpen } = useVendorLoginDialogStore();
    const [activeMenu, setActiveMenu] = useState('SHOP3');

    // 메뉴별 컨텐츠 렌더링
    const renderContent = () => {
        switch (activeMenu) {
            case 'SHOP3':
                return (
                    <>
                        <HeroSection />
                        <ImageBannerSection />
                        <AIStudioSection />
                        <CutTypeSection />
                        <Section3 />
                        <Section4 />
                    </>
                );
            case 'Market':
                return <MarketSection />;
            case 'Business':
                return <BusinessSection />;
            case 'Problem':
                return <ProblemSection />;
            case 'Solution':
                return <SolutionSection />;
            case 'Customer':
                return <CustomerSection />;
            case 'Product':
                return <ProductSection />;
            case 'Team':
                return <TeamSection />;
            default:
                return <PagePlaceholder pageName={activeMenu} />;
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Global
                styles={css`
                    @font-face {
                        font-family: 'Century Old Style';
                        src: url(${CenturyOldStyleFont}) format('opentype');
                        font-weight: normal;
                        font-style: normal;
                    }

                    * {
                        font-family: 'Century Old Style', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
                            'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
                    }
                `}
            />
            <TopNavigation activeMenu={activeMenu} onMenuChange={setActiveMenu} />
            {renderContent()}
            <FooterSection />

            {/* Dialog 컴포넌트들 */}
            <Shop3LoginDialog />
            <VendorLoginDialog isOpen={isVendorLoginOpen} onClose={() => setVendorLoginOpen(false)} />
        </Box>
    );
};

export default Main2;
